import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import Link from 'theme/atoms/Link'
import LogoutIcon from 'assets/logout.svg'
import useAccount from 'hooks/account/useAccount'
import type { PageType } from '../AccountDashboard'
import useTranslations from 'hooks/useTranslations'
import SidebarImage from './SidebarImage'

export type Props = {
  activePage: PageType
}

export default function DesktopSidebar(props: Props) {
  const t = useTranslations<'app-AccountDashboard'>()
  const account = useAccount()

  const name = account.context
    ? account.context.firstName + ' ' + account.context.lastName
    : ''

  return (
    <Wrapper className="DesktopSidebar">
      <div className="navigation">
        <div className="title">{t('dashboard_my_account')}</div>
        <div className="links">
          <NavItem
            to="/account/orders/"
            $active={props.activePage === 'orders'}
          >
            {t('dashboard_orders')}
          </NavItem>
          {/* <NavItem to='/account/'>Angebote</NavItem> */}
          {/* <NavItem to='/account/'>Retouren</NavItem> */}
          <NavItem
            to="/account/personal/"
            $active={props.activePage === 'personal'}
          >
            {t('dashboard_my_data')}
          </NavItem>
          <NavItem
            to="/account/addresses/"
            $active={props.activePage === 'addresses'}
          >
            {t('dashboard_addresses')}
          </NavItem>
          <NavItem to="/wishlist/">{t('dashboard_wishlist')}</NavItem>
          {t('dashboard_returns', {
            RETURNS: '-',
            __transformers: {
              RETURNS: (_, vars) => (
                <NavItem to={vars.RETURNS_link} blank={false}>
                  {vars.RETURNS_label}
                </NavItem>
              )
            }
          })}
        </div>
        <div className="title">{t('dashboard_navigation_my_orders')}</div>
        <div className="links">
          <NavItem to="/online-service/" blank>
            {t('dashboard_online_service_2')}
          </NavItem>
          <NavItem to="/online-service/" blank>
            {t('dashboard_online_service_3')}
          </NavItem>
          <NavItem to="/online-service/" blank>
            {t('dashboard_online_service_4')}
          </NavItem>
        </div>
      </div>

      <div className="infos">
        <div className="info">
          <div className="label">{t('dashboard_log_in_as')}</div>
          <div className="value">{name}</div>
        </div>
        {!account.context?.customerNumber.startsWith('SW') && (
          <div className="info" data-cy-state="show-customer-number">
            <div className="label">{t('dashboard_customer_no')}</div>
            <div className="value">{account.context?.customerNumber}</div>
          </div>
        )}
      </div>
      <div
        className="logout"
        onClick={account.logout}
        data-cy-handle="logout-btn"
      >
        <LogoutIcon />
        {t('dashboard_log_out')}
      </div>

      <div className="service-box">
        {t('dashboard_sidebar_image1', {
          IMAGE: '_',
          __transformers: {
            IMAGE: (_, vars) => (
              <SidebarImage
                alt={vars.IMAGE_alt}
                src={vars.IMAGE_path}
                link={vars.IMAGE_link}
                title={t('dashboard_help_and_service')}
                description={t('dashboard_order_info_text')}
              />
            )
          }
        })}
      </div>
      <div className="service-box">
        {t('dashboard_sidebar_image2', {
          IMAGE: '_',
          __transformers: {
            IMAGE: (_, vars) => (
              <SidebarImage
                alt={vars.IMAGE_alt}
                src={vars.IMAGE_path}
                link={vars.IMAGE_link}
                title={t('dashboard_payback_program')}
                description={t('dashboard_payback_program_text')}
              />
            )
          }
        })}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  > .navigation {
    gap: 1.25rem;
    display: flex;
    flex-direction: column;
    padding: ${theme.spacing('m')};
    background: ${theme.colors.shade.b7};
    margin-bottom: ${theme.spacing('l')};

    > .title {
      ${theme.ty('rc-2xl')}
    }

    > .links {
      display: flex;
      flex-direction: column;
      gap: ${theme.spacing('s')};
    }
  }

  > .infos {
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing('xs')};
    margin-bottom: ${theme.spacing('m')};
    > .info {
      display: flex;
      > .label {
        ${theme.ty('rc-base', 'bold')}
        margin-right: ${theme.spacing('xs')};
      }
      > .value {
        ${theme.ty('rc-base')}
      }
    }
  }

  > .logout {
    ${theme.ty('rc-s', 'bold')}
    text-decoration: underline;
    display: flex;
    align-items: center;
    cursor: pointer;
    margin-bottom: ${theme.spacing('l')};
    > svg {
      margin-right: ${theme.spacing('xxs')};
    }
  }

  > .service-box {
    margin-bottom: ${theme.spacing('m')};
    background: ${theme.colors.shade.b7};
    padding: ${theme.spacing('m')};
    display: flex;
  }
`

const NavItem = styled(Link)<{ $active?: boolean }>`
  ${theme.ty('rc-base')}
  color: ${(props) =>
    props.$active ? theme.colors.primary : theme.colors.shade.b2};
`
